import { memo, useCallback, useRef, useState } from 'react';

import { Pen, Undo2 } from 'lucide-react';
import Path from 'path-to-regexp';
import { useHistory } from 'react-router';

import { Button } from '@/shadcn/button';
import { Input } from '@/shadcn/input';

import type { GroupPatch, GroupSettingsQueryQuery } from '@/apollo/graphql';
import {
  getDownloadURL,
  handleUploadFileOnError,
  uploadFile
} from '@/client/media';
import { UploadAvatar } from '@/components/ui/uploadAvatar/uploadAvatar';
import { ROUTE_WORKFLOW_GROUPS } from '@/constant/routes';
import { TestId } from '@/types/testId';
import { useInput } from '@/utils/hooks';

type Props = {
  group: GroupSettingsQueryQuery['groupByRowId'];
  updateGroup: (group: GroupPatch) => void;
  updating?: boolean;
};

export const EditableGroupFields = memo(
  ({ group, updateGroup, updating = false }: Props) => {
    const { name, avatar } = group ?? {};

    const history = useHistory();

    // go back to workflow groups page
    const goBack = useCallback(() => {
      history.push(
        Path.compile(ROUTE_WORKFLOW_GROUPS)({
          id: group?.workflow?.rowId
        })
      );
    }, [history, group]);

    const groupNameInputRef = useRef<HTMLInputElement>(null);
    const [inputGroupName, setInputGroupName] = useInput(name);

    const updateGroupName = useCallback(() => {
      if (inputGroupName !== name) updateGroup({ name: inputGroupName });
    }, [updateGroup, name, inputGroupName]);

    const [uploadingAvatar, setUploadingAvatar] = useState(false);

    const updateAvatar = useCallback(
      newAvatarFile => {
        setUploadingAvatar(true);
        uploadFile(
          newAvatarFile,
          async savedFile => {
            const avatarUrl = getDownloadURL(savedFile);
            updateGroup({ avatar: avatarUrl });
            setUploadingAvatar(false);
          },
          () => {
            setUploadingAvatar(false);
            handleUploadFileOnError(
              newAvatarFile.name,
              newAvatarFile.response.status
            );
          },
          null,
          true
        );
      },
      [updateGroup]
    );

    const hasChanged = inputGroupName !== name;

    return (
      <div className="flex items-center gap-3">
        <Button
          className="mr-3"
          onClick={goBack}
          size="icon"
          variant="ghostSubtle"
        >
          <Undo2 />
        </Button>
        <UploadAvatar
          avatar={avatar}
          data-testid={TestId.EditableGroupFields.UploadAvatar}
          loading={updating || uploadingAvatar}
          name={inputGroupName}
          submitNewAvatar={updateAvatar}
        />
        <Input
          data-testid={TestId.EditableGroupFields.GroupNameInput}
          onChange={setInputGroupName}
          ref={groupNameInputRef}
          spellCheck="false"
          type="text"
          value={inputGroupName}
        />
        {hasChanged && (
          <Button
            className="aspect-square"
            data-testid={TestId.EditableGroupFields.UpdateGroupNameButton}
            onClick={updateGroupName}
            size="icon"
          >
            <Pen />
          </Button>
        )}
      </div>
    );
  }
);
